import React, { useContext, useState } from "react"
import Header from "../../layouts/Header"
import { styled, createTheme, ThemeProvider } from "@mui/material/styles"
import { Box } from "@mui/material"
import BackIcon from "../../components/BackIcon"
import { useNavigate } from "react-router-dom"
import CustomConditionalActionsTableServer from "../../components/CustomConditionalActionsTableServerSide"
import normalFormatDate from "../utils/date/DDMMYYYFormatDate"
import { GlobalContext } from "../../contexts/GlobalContext"
import { useMutation, api } from "../../api"
import getIconComponent from "../utils/getIconComponent"
import CustomBackdrop from "../../components/CustomBackdrop"
import { useQueryClient } from "@tanstack/react-query"
import {
  handleExportDataPdfLGScreen,
  handleExportDataPdfSMScreen,
  handleAllExportDataCSV,
} from "../utils/reactTableActions/exportToolbarActions"
import ModalImportCSV from "../../components/CustomCSVImportButton"
import Swal from "sweetalert2"

const StyledRoot = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  marginTop: theme.spacing(8),
  flexGrow: 1,
  padding: "0 16px",
  height: "100vh",
}))

const theme = createTheme({
  palette: {
    primary: {
      main: "#196C87",
    },
    secondary: {
      main: "#196C87",
    },
  },
})

const TipoTransaccionesIndex = () => {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const { selectedMenuInfo } = useContext(GlobalContext)
  const [openModal, setOpenModal] = useState(false)

  // Mutación para eliminar el registro
  const { mutateAsync: SaveEliminacionTransaccion, isPending: isDeletingTransaccion } = useMutation({
    queryKey: ["isDeletingTipoTransaccion"],
    fn: async (data) => {
      const response = await api.post("/TipoTransacciones/eliminarTipoTransacciones", data)
      return response.data
    },
    showError: "modal",
    showSuccess: "toast",
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["TipoTransacciones"] })
    },
  })

  // Función para manejar el modal de confirmación antes de eliminar
  const handleDelete = async (row) => {
    const confirmacion = await Swal.fire({
      title: "¿Eliminar Tipo de Transacción?",
      html: `<p>¿Está seguro que desea eliminar el tipo de transacción <strong>${row.original.tracodigo} - ${row.original.tradescri}</strong>?</p>
             <p style="font-size: 0.9em; color: #666;">Esta acción no se puede deshacer.</p>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#d32f2f",
      cancelButtonColor: "#196C87",
      reverseButtons: true,
    })

    if (confirmacion.isConfirmed) {
      try {
        await SaveEliminacionTransaccion(row.original)
      } catch (err) {
        console.error("Error eliminando el Tipo de Transacción:", err)
      }
    }
  }

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 30px 30px 30px",
            fontSize: "25px",
          }}
        >
          <b>Gestión de Tipos de Transacciones</b>
        </div>

        <CustomBackdrop isLoading={isDeletingTransaccion} />

        <Box className={StyledRoot}>
          {/* MODAL DE IMPORTACIÓN CSV */}
          <ModalImportCSV
            open={openModal}
            onClose={() => setOpenModal(false)}
            templateFileName="Plantilla_TiposTransacciones.csv"
            fieldConfigs={{
              tracodigo: { required: true, key: true },
              tradescri: { required: true },
              tratipo: { required: true },
              trasigno: { required: true },
              trastatus: { required: false },
            }}
            maxFileSize={10 * 1024 * 1024}
            validateEndpoint="/TipoTransacciones/validarTipoTransaccionesIMP"
            insertEndpoint="/TipoTransacciones/insertarTipoTransaccionesIMP"
            onImportComplete={() => qc.invalidateQueries({ queryKey: ["TipoTransacciones"] })}
          />

          {/* GRILLA PRINCIPAL */}
          <CustomConditionalActionsTableServer
            endpoint="/TipoTransacciones/getAllTipoTransacciones"
            errorMsgFilterSearch="Error al cargar datos de Tipos de Transacciones"
            queryKeyModal="TipoTransacciones"
            perPage={10}
            rowActionsWidthTable={120}
            rowActions={(row) => {
              const editarAction = selectedMenuInfo?.data?.barraAcciones?.find(
                (action) => action?.acccaption === "EDITAR",
              )
              const eliminarAction = selectedMenuInfo?.data?.barraAcciones?.find(
                (action) => action?.acccaption === "ELIMINAR",
              )

              const actions = []

              if (editarAction) {
                actions.push({
                  label: "Editar",
                  key: "EDITAR",
                  icon: getIconComponent(editarAction?.accnameicono, editarAction?.acctipoico),
                  onClick: (row) => navigate("editar", { state: row.original }),
                })
              }

              if (eliminarAction) {
                actions.push({
                  label: "Eliminar",
                  key: "ELIMINAR",
                  icon: getIconComponent(eliminarAction?.accnameicono, eliminarAction?.acctipoico),
                  onClick: (row) => handleDelete(row),
                })
              }

              return actions
            }}
            topToolbarCustomActions={({ table, device }) => {
              const crearAction = selectedMenuInfo?.data?.barraAcciones?.find((action) => action.acccaption === "CREAR")
              const exportarAction = selectedMenuInfo?.data?.barraAcciones?.find(
                (action) => action.acccaption === "EXPORTAR",
              )
              const importarAction = selectedMenuInfo?.data?.barraAcciones?.find(
                (action) => action.acccaption === "IMPORTAR",
              )

              const toolbarActions = []
              if (crearAction) {
                toolbarActions.push({
                  label: crearAction?.acccaption,
                  key: crearAction?.acccaption,
                  icon: getIconComponent(crearAction?.accnameicono, crearAction?.acctipoico),
                  onClick: () => navigate("crear"),
                })
              }
              if (exportarAction) {
                toolbarActions.push({
                  type: "dropdown",
                  label: exportarAction?.acccaption,
                  key: "exportarDropdown",
                  icon: getIconComponent(exportarAction?.accnameicono, exportarAction?.acctipoico),
                  actions: [
                    {
                      label: "Exportar PDF",
                      key: "exportarPDF",
                      icon: getIconComponent(exportarAction?.accnameicono, exportarAction?.acctipoico),
                      onClick: ({ columns, data }) => {
                        const title = "Reporte de Transacciones"
                        if (device === "sm") {
                          return handleExportDataPdfSMScreen(
                            columns,
                            data,
                            title,
                            `${title} ${new Date().toLocaleString()}`,
                          )
                        }
                        handleExportDataPdfLGScreen(
                          columns,
                          table.getCoreRowModel().rows,
                          title,
                          `${title} ${new Date().toLocaleString()}`,
                        )
                      },
                    },
                    {
                      label: "Exportar CSV",
                      key: "exportarCSV",
                      icon: getIconComponent(exportarAction?.accnameicono, exportarAction?.acctipoico),
                      onClick: ({ data }) =>
                        handleAllExportDataCSV(data, `Grupos_Productos_${new Date().toLocaleString()}`),
                    },
                  ],
                })
              }
              if (importarAction) {
                toolbarActions.push({
                  label: importarAction?.acccaption || "Importar",
                  key: "importarBtn",
                  icon: getIconComponent(
                    importarAction?.accnameicono || "UploadFile",
                    importarAction?.acctipoico || "MaterialIcons",
                  ),
                  onClick: () => setOpenModal(true),
                })
              }
              return toolbarActions
            }}
            columnsTable={[
              {
                accessorKey: "tracodigo",
                header: "Código",
                size: 90,
                Cell: ({ cell }) => <span>{cell.getValue()}</span>,
              },
              {
                accessorKey: "tradescri",
                header: "Descripción",
                size: 240,
                Cell: ({ cell }) => <span>{cell.getValue()}</span>,
              },
              {
                accessorKey: "tratipo",
                header: "Tipo",
                size: 110,
                Cell: ({ cell }) => <span>{cell.getValue()}</span>,
              },
              {
                accessorKey: "trasigno",
                header: "Signo",
                size: 80,
                Cell: ({ cell }) => <span>{cell.getValue()}</span>,
              },
              {
                accessorKey: "pctacodigo",
                header: "Cuenta Contable",
                size: 140,
                Cell: ({ cell }) => <span>{cell.getValue()}</span>,
              },
              {
                accessorKey: "trastatus",
                header: "Estado",
                size: 100,
                Cell: ({ cell }) => {
                  const valor = cell.getValue()
                  return <span>{valor === "A" ? "ACTIVO" : "INACTIVO"}</span>
                },
              },
              {
                accessorKey: "trafecisys",
                header: "Fecha Creación",
                size: 150,
                Cell: ({ cell }) => {
                  const value = cell.getValue()
                  return <span>{normalFormatDate(value)}</span>
                },
              },
              {
                accessorKey: "trausuisys",
                header: "Creado Por",
                size: 110,
                Cell: ({ cell }) => <span>{cell.getValue()}</span>,
              },
            ]}
          />
        </Box>
      </div>
    </ThemeProvider>
  )
}

export default TipoTransaccionesIndex
